import { useState, useEffect } from 'react';
import { getWinners, getCompletedDraws } from '../lib/api';
import { useLang } from '../lib/lang';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import InlineEditable from '../components/InlineEditable';
import WinnerCard from '../components/WinnerCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Star, Calendar, Gift } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

export default function WinnersPage() {
  const { t } = useLang();
  const [winners, setWinners] = useState([]);
  const [completedDraws, setCompletedDraws] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [w, d] = await Promise.all([getWinners(), getCompletedDraws()]);
        setWinners(w.data.winners || []);
        setCompletedDraws(d.data.draws || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-rolex-green"><div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const totalPrizeValue = winners.reduce((sum, w) => sum + (Number(w.product_value) || 0), 0);


  const renderWinnerCard = (w, i) => (
    <motion.div key={i} variants={fadeUp} data-testid="winner-card" className={winners.length === 1 ? 'w-full max-w-sm' : ''}>
      <WinnerCard w={w} showDate />
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-rolex-green relative overflow-hidden" data-testid="winners-page">
      <div className="absolute inset-0 hero-spotlight opacity-50 pointer-events-none" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-[radial-gradient(circle,hsl(var(--gold)/0.10),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        <div className="text-center mb-10">
          <p className="gold-line mb-4 mx-auto justify-center"><Star className="w-3 h-3" /> <InlineEditable tKey="hallOfWinners">{t('hallOfWinners') || 'Hall of Winners'}</InlineEditable></p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-cream leading-[1.05]"><InlineEditable tKey="winnersHistory">{t('winnersHistory')}</InlineEditable></h1>
          <p className="text-cream/60 mt-4"><InlineEditable tKey="realPeopleRealPrizes">{t('realPeopleRealPrizes')}</InlineEditable></p>
        </div>

        <Tabs defaultValue="winners">
          <TabsList className="bg-[hsl(var(--rolex-green-elev)/0.5)] backdrop-blur-sm border border-[hsl(var(--gold)/0.20)] mb-10 mx-auto flex w-fit p-1.5 h-auto">
            <TabsTrigger value="winners" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Trophy className="w-4 h-4" /> <InlineEditable tKey="recentWinners">{t('recentWinners')}</InlineEditable></TabsTrigger>
            <TabsTrigger value="history" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Calendar className="w-4 h-4" /> <InlineEditable tKey="completedDraws">{t('completedDraws')}</InlineEditable></TabsTrigger>
          </TabsList>

          <TabsContent value="winners">
            {winners.length === 0 ? (
              <div className="text-center py-16 text-cream/50"><Trophy className="w-12 h-12 mx-auto mb-3 opacity-30 text-gold" /><p>No winners yet</p></div>
            ) : (
              <motion.div initial="hidden" animate="visible" variants={stagger} className={winners.length === 1 ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'}>
                {winners.map(renderWinnerCard)}
              </motion.div>
            )}
          </TabsContent>

          <TabsContent value="history">
            {completedDraws.length === 0 ? (
              <div className="text-center py-16 text-cream/50"><Gift className="w-12 h-12 mx-auto mb-3 opacity-30 text-gold" /><p><InlineEditable tKey="noCompletedDraws">{t('noCompletedDraws')}</InlineEditable></p></div>
            ) : (
              <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-4 max-w-4xl mx-auto">
                {completedDraws.map((draw, i) => (
                  <motion.div key={i} variants={fadeUp} className="rounded-2xl bg-rolex-green-deep border border-gold/15 overflow-hidden">
                    <div className="p-5 flex items-center gap-5">
                      <img src={draw.images?.[0]} alt={draw.title} className="w-20 h-20 rounded-xl object-cover border border-gold/20 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-lg text-cream truncate">{draw.title}</h3>
                        <p className="text-sm text-cream/60"><InlineEditable tKey="value">{t('value')}</InlineEditable>: <span className="font-mono text-gold font-semibold">£{draw.product_value?.toLocaleString()}</span></p>
                      </div>
                      {draw.winner && (
                        <div className="text-right shrink-0">
                          <span className="inline-flex items-center rounded-full bg-gold/15 border border-gold/30 text-gold px-3 py-1 text-xs font-bold"><InlineEditable tKey="winner">{t('winner')}</InlineEditable>: {draw.winner.user_name}</span>
                          <p className="text-[11px] text-cream/45 mt-1.5"><InlineEditable tKey="drawnOn">{t('drawnOn')}</InlineEditable> {draw.winner.drawn_at ? new Date(draw.winner.drawn_at).toLocaleDateString() : ''}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
